/**
 * useUiState Hook - UI State Management
 *
 * Custom hook that provides UI state and toggle actions using atomic Zustand selectors.
 * This hook follows Zustand best practices by subscribing to primitive boolean values,
 * ensuring minimal re-renders and optimal performance.
 *
 * @example
 * ```tsx
 * const { isSidebarOpen, isSidePanelOpen, toggleSidebar } = useUiState();
 *
 * if (isSidebarOpen) {
 *   // Render sidebar
 * }
 *
 * toggleSidebar(); // Toggle sidebar open/closed
 * closeAll(); // Close all UI panels
 * ```
 *
 * Performance:
 * - 2 atomic subscriptions (boolean primitives only)
 * - Each subscription re-renders ONLY when its specific boolean changes
 * - Action subscriptions (stable references) - never cause re-renders
 */

import { useGeoMarkupStore } from '../store';

/**
 * Custom hook for UI state management
 * Uses atomic selectors for optimal performance
 */
export const useUiState = () => {
  // ===== ATOMIC SUBSCRIPTIONS =====
  // Subscribe to boolean primitives only
  // Each subscription re-renders ONLY when its specific value changes

  const isSidebarOpen = useGeoMarkupStore((s) => s.ui.sidebar.isOpen);
  const isSidePanelOpen = useGeoMarkupStore((s) => s.ui.sidePanel.isOpen);

  // ===== ACTIONS =====
  // Stable function references - never cause re-renders

  const toggleSidebar = useGeoMarkupStore((s) => s.ui.toggleSidebar);
  const toggleSidePanel = useGeoMarkupStore((s) => s.ui.toggleSidePanel);
  const closeAll = useGeoMarkupStore((s) => s.ui.closeAll);

  return {
    // State (boolean primitives)
    isSidebarOpen,
    isSidePanelOpen,

    // Actions
    toggleSidebar,
    toggleSidePanel,
    closeAll,
  };
};
